import { queryKeyPrefix } from '@src/corelib/query/utils';
import { useMutation } from '@src/query';


/**
 * Pick/pull-checklist mutation hooks (#269). Each wraps the shared `useMutation`
 * engine with the injected-`queryClient` `mutateFn({ apiClient, queryClient })`
 * contract — the corelib query singleton is NOT imported here. Invalidation runs
 * inside `mutateFn` (the one place `queryClient` is injected) so the exact cache
 * key prefix for the list query is preserved.
 *
 * The list is read with `useQuery('/sales/pick-list/<sellerId>/')`, whose key is
 * derived as `[namespace, ...url.split('/')]` (see `corelib/query/useBaseQuery.js`
 * + `urlToQueryKey`). That yields `['root','','sales','pick-list',sellerId,'']`;
 * the prefix below `['root','','sales','pick-list',sellerId]` matches it (TanStack
 * matches a key by any leading prefix). Mirrors the Leads canned-response module.
 */

/** Cache key prefix for a seller's pick-list. */
function pickListKeyPrefix(sellerId) {
  return queryKeyPrefix('sales', 'pick-list', String(sellerId || ''));
}


/** Mark a sale as pulled. Variables: `{ sellerId, saleId }`. */
function usePullSaleMutation(params) {
  return useMutation({
    mutateFn: async ({ apiClient, queryClient, sellerId, saleId }) => {
      const data = await apiClient.request({
        url: `/sales/pick-list/${sellerId}/${saleId}/pull/`,
        method: 'POST',
      });
      queryClient.invalidateQueries({ queryKey: pickListKeyPrefix(sellerId) });
      return data;
    },
    ...params,
  });
}


/** Revert a pulled sale. Variables: `{ sellerId, saleId }`. */
function useUnpullSaleMutation(params) {
  return useMutation({
    mutateFn: async ({ apiClient, queryClient, sellerId, saleId }) => {
      const data = await apiClient.request({
        url: `/sales/pick-list/${sellerId}/${saleId}/unpull/`,
        method: 'POST',
      });
      queryClient.invalidateQueries({ queryKey: pickListKeyPrefix(sellerId) });
      return data;
    },
    ...params,
  });
}


export { pickListKeyPrefix, usePullSaleMutation, useUnpullSaleMutation };
